'use client';

import Layout from '@/components/layout';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { supabaseClient } from '@/lib/_utils/supabase/client';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import AuthBlock from '@/components/authBlock/AuthBlock.tsx';
import useValidator from '@/hooks/validator.hook';
import { Button } from '@/components/button/Button';
import useToast from '@/hooks/toast.hook';
import { Icon } from '@/components/icon/Icon';
import useTralentStore from '@/stores';
import { regexPassword } from '@/lib/_utils';
import { routeInfo } from '@/lib/_utils/routing';

export default function SigninPageClient() {
	const { t } = useTranslation('common');
	const { openToast } = useToast();
	const router = useRouter();
	const { setBalance } = useTralentStore();
	const [loading, setLoading] = useState<boolean>(false);
	const [showPassword, setShowPassword] = useState(false);

	interface SignInForm {
		email: string;
		password: string;
	}

	const schema = yup.object().shape({
		email: yup.string().required().email(),
		password: yup.string().required().matches(regexPassword, 'Invalid password'),
	});

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({ resolver: yupResolver(schema) });

	useValidator(errors);

	/**
	 * Handle • sign in
	 * @param params
	 */
	const handleSignIn = async (params: SignInForm) => {
		setLoading(true);
		const { data, error } = await supabaseClient.auth.signInWithPassword({
			email: params.email,
			password: params.password,
		});

		if (error) {
			openToast({
				text: t('validations:general'),
				fields: [error.message],
				status: 'danger',
				time: 3000,
			});
		} else {
			setBalance(data?.user?.app_metadata?.balance ?? 0);
			router.push('/dashboard');
		}
		setLoading(false);
	};

	return (
		<Layout
			fullscreen
			responsive
			style={{ global: 'bg_square' }}
			gtmEventTrackingProps={{ event: 'pageview_event', var_pagetype: 'auth_signin' }}>
			<div className={`align-center flex h-full flex-col justify-center p-[30px] sm:p-[24px]`}>
				<AuthBlock title={t('sign_in')}>
					<form onSubmit={handleSubmit(handleSignIn)} className={`form-content`}>
						<div className={`item-row`}>
							<div className={`form-field floating-placeholder ${errors.email ? 'field-err' : ''}`}>
								<input
									type='email'
									placeholder={t('email')}
									{...register('email')}
									className={`${errors.email ? 'err' : ''}`}
								/>
								<div className={'placeholder'}>{t('email')}</div>
							</div>
						</div>
						<div className={`item-row`}>
							<div
								className={`form-field form-field-icon-right floating-placeholder ${
									errors.password ? 'field-err' : ''
								}`}>
								<input
									type={showPassword ? 'text' : 'password'}
									placeholder={t('password')}
									{...register('password')}
									className={`${errors.password ? 'err' : ''}`}
								/>
								<div className={'placeholder'}>{t('password')}</div>
								<div
									className={`icon-right mx-2 cursor-pointer ${showPassword ? '' : ''}`}
									onClick={() => setShowPassword(!showPassword)}>
									{!showPassword ? <Icon name='eyeOpenOutlined' /> : <Icon name='eyeClosedOutlined' />}
								</div>
							</div>
							<div className={`mt-[8px] text-right`}>
								<Link href={routeInfo({ t, page: 'forgetPassword' }).path} className={`link`}>
									{routeInfo({ t, page: 'forgetPassword' }).label}?
								</Link>
							</div>
						</div>
						<div className='item-row flex justify-center'>
							<Button variant='primary' fullwidth={true} loading={loading}>
								{t('sign_in')}
							</Button>
						</div>
						<div className={`item-row flex`}>
							<span className={`mr-[6px] font-medium`}>Don’t have an account yet?</span>
							<Link href={routeInfo({ t, page: 'signup' }).path} className={`link`}>
								{routeInfo({ t, page: 'signup' }).label} ›
							</Link>
						</div>
					</form>
				</AuthBlock>
			</div>
		</Layout>
	);
}
